import { ChangeDetectionStrategy, Component, DestroyRef, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { BriefApplicationResponseDto, BriefResponseDto } from '@/app/common/general-types/briefs.dto';
import { PayoutDto } from '@/app/common/general-types/payouts.dto';
import { WalletTransactionResponseDto } from '@/app/common/general-types/wallet-transaction.dto';
import {
  applicationStatusLabel,
  payoutProviderLabel,
  payoutStatusLabel,
  walletTransactionStatusLabel,
  walletTransactionTypeLabel,
} from '../../admin-payout-labels/admin-payout-labels';
import { PayoutTraceResult } from '../payout-trace.types';
import { AdminPayoutTraceService } from '../../../services/admin-payout-trace.service';
import { AdminSettingsContextService } from '../../../services/admin-settings-context.service';
import { autoLoadOnContextKey } from '../../../services/admin-settings-auto-load';
import { formatPayoutTraceCreatedDate } from './payout-trace-checklist.helpers';

export type TraceStepState = 'idle' | 'loading' | 'ok' | 'missing' | 'error' | 'skipped';

export interface PayoutTraceStep {
  key: string;
  title: string;
  state: TraceStepState;
  summary: string;
  details: string[];
  createdDate?: string;
}

@Component({
  selector: 'admin-payout-trace-checklist',
  imports: [],
  templateUrl: './payout-trace-checklist.html',
  styleUrl: './payout-trace-checklist.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayoutTraceChecklist {
  traceCompleted = output<PayoutTraceResult | null>();

  readonly settingsContext = inject(AdminSettingsContextService);

  traceLoading = signal(false);
  steps = signal<PayoutTraceStep[]>(this.initialSteps());

  private destroyRef = inject(DestroyRef);
  private traceService = inject(AdminPayoutTraceService);
  private pendingTrace: Partial<PayoutTraceResult> = {};

  constructor() {
    autoLoadOnContextKey(() => {
      const briefId = this.settingsContext.briefId().trim();
      const creatorId = this.settingsContext.creatorId().trim();
      return briefId && creatorId ? `${briefId}|${creatorId}` : null;
    }, () => this.runTrace());
  }

  runTrace(): void {
    const creatorId = this.settingsContext.creatorId().trim();
    const briefId = this.settingsContext.briefId().trim();
    if (!creatorId || !briefId) {
      return;
    }

    this.traceLoading.set(true);
    this.traceCompleted.emit(null);
    this.pendingTrace = { creatorId, briefId };
    this.steps.set(
      this.initialSteps().map((s) => ({ ...s, state: 'loading' as const, summary: 'Checking…', details: [] })),
    );

    this.traceService
      .getCreator(creatorId)
      .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
      .subscribe((creator) => {
        this.patchStep(
          'creator',
          creator
            ? {
                state: 'ok',
                summary: 'Found',
                ...this.withCreatedDate(
                  [
                    `${creator.firstName ?? ''} ${creator.lastName ?? ''}`.trim() || '—',
                    creator.email ?? '—',
                    `blazeFirebaseUid: ${creator.blazeFirebaseUid ?? creatorId}`,
                  ],
                  creator.createdDate,
                ),
              }
            : { state: 'missing', summary: 'Not found', details: [`No creator for id: ${creatorId}`] },
        );

        this.traceService
          .getBrief(briefId)
          .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
          .subscribe((brief) => {
            const briefCurrency = this.resolveBriefCurrency(brief);
            this.pendingTrace = { ...this.pendingTrace, briefCurrencyCode: briefCurrency };

            this.patchStep(
              'brief',
              brief
                ? {
                    state: 'ok',
                    summary: 'Found',
                    ...this.withCreatedDate(
                      [brief.title ?? '—', `id: ${brief.id ?? briefId}`, `currency: ${briefCurrency}`],
                      brief.createdDate,
                    ),
                  }
                : { state: 'missing', summary: 'Not found', details: [`No brief for id: ${briefId}`] },
            );

            this.traceService
              .getBriefApplication(briefId, creatorId)
              .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
              .subscribe((application) => {
                this.handleApplicationStep(application, creatorId, briefId);
              });
          });
      });
  }

  /** Re-fetch payout step after create/retry without re-running the full trace. */
  refreshPayout(payoutHint?: PayoutDto | null): void {
    const currency = (this.pendingTrace.currencyCode ?? this.pendingTrace.briefCurrencyCode ?? 'GBP').toUpperCase();
    const amount = this.pendingTrace.amount ?? 0;

    if (payoutHint?.id) {
      this.applyPayoutStep(payoutHint, currency, amount);
      return;
    }

    const walletTransactionId = this.pendingTrace.walletTransactionId;
    const payoutId = this.pendingTrace.payoutId;

    if (!walletTransactionId && !payoutId) {
      return;
    }

    this.patchStep('payout', { state: 'loading', summary: 'Refreshing…', details: [], createdDate: undefined });

    const request$ = payoutId
      ? this.traceService.getPayoutById(payoutId)
      : this.traceService.getPayoutByWalletTransactionId(walletTransactionId!);

    request$
      .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
      .subscribe((payout) => this.applyPayoutStep(payout, currency, amount));
  }

  private handleApplicationStep(
    application: BriefApplicationResponseDto | null,
    creatorId: string,
    briefId: string,
  ): void {
    if (!application?.id) {
      this.patchStep('briefApplication', {
        state: 'missing',
        summary: 'Not found',
        details: [`No application for brief ${briefId} and creator ${creatorId}`],
      });
      this.skipWalletAndPayout('Brief application required first');
      this.finishTrace(null);
      return;
    }

    this.pendingTrace = {
      ...this.pendingTrace,
      briefApplicationId: application.id,
    };

    this.patchStep('briefApplication', {
      state: 'ok',
      summary: 'Found',
      ...this.withCreatedDate(
        [
          `id: ${application.id}`,
          `status: ${applicationStatusLabel(application.applicationStatus)} (${application.applicationStatus})`,
        ],
        application.createdDate,
      ),
    });

    this.traceService
      .getWalletTransactionByBriefApplicationId(application.id)
      .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
      .subscribe((walletTx) => {
        if (!walletTx?.id) {
          this.patchStep('walletTransaction', {
            state: 'missing',
            summary: 'Not found',
            details: [`No wallet transaction for briefApplicationId ${application.id}`],
          });
          this.skipWalletAndPayout('Wallet transaction required for payout');
          this.finishTrace(null);
          return;
        }

        const currency = (walletTx.currencyCode ?? this.pendingTrace.briefCurrencyCode ?? 'GBP').toUpperCase();
        const amount = Math.abs(Number(walletTx.amount ?? 0));

        this.pendingTrace = {
          ...this.pendingTrace,
          walletTransactionId: walletTx.id,
          currencyCode: currency,
          amount,
        };

        const amountMajor = amount / 100;
        this.patchStep('walletTransaction', {
          state: 'ok',
          summary: 'Found',
          ...this.withCreatedDate(
            [
              `id: ${walletTx.id}`,
              `type: ${walletTransactionTypeLabel(walletTx.type)} (${walletTx.type ?? '—'})`,
              `status: ${walletTransactionStatusLabel(walletTx.status)} (${walletTx.status ?? '—'})`,
              `amount: ${amountMajor} ${currency}`,
            ],
            walletTx.createdDate,
          ),
        });

        this.traceService
          .getPayoutByWalletTransactionId(walletTx.id)
          .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
          .subscribe((payout) => this.applyPayoutStep(payout, currency, amount, true));
      });
  }

  private applyPayoutStep(
    payout: PayoutDto | null,
    currency: string,
    amount: number,
    finishRun = false,
  ): void {
    if (!payout?.id) {
      this.patchStep('payout', {
        state: 'missing',
        summary: 'Not found',
        details: ['No payout linked to this wallet transaction yet'],
      });
      if (finishRun) {
        this.finishTrace(this.buildTraceResult());
      } else {
        this.emitTraceResult();
      }
      return;
    }

    const amountMajor = Math.abs(Number(payout.amount ?? amount)) / 100;
    this.pendingTrace = { ...this.pendingTrace, payoutId: payout.id };

    this.patchStep('payout', {
      state: 'ok',
      summary: 'Found',
      ...this.withCreatedDate(
        [
          `id: ${payout.id}`,
          `provider: ${payoutProviderLabel(payout.provider)}`,
          `status: ${payoutStatusLabel(payout.payoutStatus)} (${payout.payoutStatus ?? '—'})`,
          `amount: ${amountMajor} ${currency}`,
        ],
        payout.createdDate,
      ),
    });

    if (finishRun) {
      this.finishTrace(this.buildTraceResult());
    } else {
      this.emitTraceResult();
    }
  }

  private emitTraceResult(): void {
    this.traceCompleted.emit(this.buildTraceResult());
  }

  private buildTraceResult(): PayoutTraceResult | null {
    const t = this.pendingTrace;
    if (!t.creatorId || !t.briefId || !t.briefApplicationId || !t.walletTransactionId) {
      return null;
    }
    return {
      creatorId: t.creatorId,
      briefId: t.briefId,
      briefApplicationId: t.briefApplicationId,
      walletTransactionId: t.walletTransactionId,
      currencyCode: t.currencyCode ?? t.briefCurrencyCode ?? 'GBP',
      briefCurrencyCode: t.briefCurrencyCode ?? t.currencyCode ?? 'GBP',
      amount: t.amount ?? 0,
      payoutId: t.payoutId,
    };
  }

  private finishTrace(result: PayoutTraceResult | null): void {
    this.traceLoading.set(false);
    this.traceCompleted.emit(result);
  }

  private resolveBriefCurrency(brief: BriefResponseDto | null): string {
    if (!brief) {
      return 'GBP';
    }
    const fromPricing = (brief.basePricingModel as { currencyCode?: string } | undefined)?.currencyCode;
    const fromCurrent = (brief.currentPrice as { currencyCode?: string } | undefined)?.currencyCode;
    return (fromPricing ?? fromCurrent ?? 'GBP').toUpperCase();
  }

  private skipWalletAndPayout(reason: string): void {
    this.patchStep('walletTransaction', {
      state: 'skipped',
      summary: 'Skipped',
      details: [reason],
      createdDate: undefined,
    });
    this.patchStep('payout', { state: 'skipped', summary: 'Skipped', details: [reason], createdDate: undefined });
  }

  private withCreatedDate(
    details: string[],
    createdDate: string | Date | null | undefined,
  ): Pick<PayoutTraceStep, 'details' | 'createdDate'> {
    const formatted = formatPayoutTraceCreatedDate(createdDate);
    if (!formatted) {
      return { details, createdDate: undefined };
    }
    return {
      details: [...details, `createdDate: ${formatted}`],
      createdDate: formatted,
    };
  }

  private patchStep(
    key: string,
    patch: Partial<Pick<PayoutTraceStep, 'state' | 'summary' | 'details' | 'createdDate'>>,
  ): void {
    this.steps.update((steps) =>
      steps.map((step) => (step.key === key ? { ...step, ...patch } : step)),
    );
  }

  private initialSteps(): PayoutTraceStep[] {
    return [
      { key: 'creator', title: 'Creator', state: 'idle', summary: '—', details: [] },
      { key: 'brief', title: 'Brief', state: 'idle', summary: '—', details: [] },
      { key: 'briefApplication', title: 'Brief application', state: 'idle', summary: '—', details: [] },
      { key: 'walletTransaction', title: 'Wallet transaction', state: 'idle', summary: '—', details: [] },
      { key: 'payout', title: 'Payout', state: 'idle', summary: '—', details: [] },
    ];
  }

  stepIconClass(state: TraceStepState): string {
    switch (state) {
      case 'ok':
        return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
      case 'missing':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
      case 'loading':
        return 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300';
      case 'skipped':
        return 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800/60 dark:text-zinc-400';
      default:
        return 'bg-zinc-50 text-zinc-400 dark:bg-zinc-900 dark:text-zinc-500';
    }
  }
}
